/**
 * **The facts a Triage item carries**, lifted off the payload once so no component anywhere holds
 * an `AttentionItemPayload`.
 *
 * The exact mirror of `feedbackShape` on the other half of the queue, and it exists for the same
 * reason: the wire's payload is one flat bag whose forty fields mean different things per reason,
 * and a card reading it directly would have to know which. Here every field is normalised to a
 * value or `null`, and `null` MEANS absent rather than empty.
 *
 * Which of these a given reason actually draws is the SURFACE's decision —
 * `pages/tasks/model/triageCard` — because the Pulpit's one-line row draws none of them and
 * `/zadania`'s card draws three. This module only says what is there.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesAttentionDtosResponsesAttentionItemPayload as AttentionItemPayload } from '~/shared/api/generated/model';

/** Every payload field a Triage reason can put on a card. All nullable, all meaning-carrying. */
export interface TriageFacts {
  /** The session's own name, where a plan day named it. */
  readonly sessionName: string | null;
  /** The CLIENT's civil day the session was owed on (R10) — never an instant. */
  readonly sessionLocalDay: string | null;
  readonly sessionSetsDone: number | null;
  readonly sessionSetsTotal: number | null;
  readonly planName: string | null;
  readonly planEndDate: string | null;
  readonly goalDescription: string | null;
  readonly goalDeadline: string | null;
  /** How long an invitation has waited (R10). */
  readonly daysPending: number | null;
  readonly messagePreview: string | null;
  /**
   * The ids of the records the item concerns — additive payload fields since 24 September 2026,
   * which is what lets a card write to them on the spot. `null` on an item from an older backend,
   * and the card then keeps its read and drops the write rather than guessing an id.
   */
  readonly planAssignmentId: number | null;
  readonly goalId: number | null;
  readonly invitationId: number | null;
  /**
   * Whether a lead's e-mail address was proven by the code (public-form R12, R20): `false` only when
   * the form sent the lead unproven. An additive payload field since 28 September 2026 — `null` on an
   * item from an older backend and on every other reason, and a card then says nothing about it.
   */
  readonly leadEmailVerified: boolean | null;
  /** What a low-sleep or low-energy item was judged on (R22, R23); `null` on every other reason. */
  readonly vital: VitalFacts | null;
  /** What a personal-record item names (R24); `null` on every other reason. */
  readonly record: RecordFacts | null;
}

/**
 * The server's own verdict on a client's recent readings — never recomputed here. `average` is the
 * figure the item was raised on, `sampleCount` how many readings it is taken over (one to three),
 * `threshold` the trainer's level it stood under. The day and the source belong to the NEWEST of
 * those readings, which is also what the item is anchored on.
 */
export interface VitalFacts {
  readonly average: number;
  readonly sampleCount: number;
  readonly threshold: number;
  /** The client's civil day of the newest reading. */
  readonly localDay: string | null;
  readonly source: 'Workout' | 'Survey' | null;
  /** The survey that reported the newest reading, where a survey did. */
  readonly surveyAssignmentId: number | null;
}

export interface RecordFacts {
  /** How many MOVEMENTS of the session hold a record — not how many sets. */
  readonly movements: number;
  /** One of them, named in the reader's content language by the server. */
  readonly exerciseName: string | null;
  /** That movement in the library, for its picture. `null` for one no longer there. */
  readonly exerciseId: number | null;
}

function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
}

function count(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function vitalFacts(payload: AttentionItemPayload | null | undefined): VitalFacts | null {
  const average = count(payload?.vitalValue);
  const sampleCount = count(payload?.vitalSampleCount);
  const threshold = count(payload?.vitalThreshold);
  // Without all three there is no verdict to state, and a card would print half of one.
  if (average === null || sampleCount === null || threshold === null) return null;
  const source = payload?.vitalSource;
  return {
    average,
    sampleCount,
    threshold,
    localDay: text(payload?.vitalLocalDay),
    source: source === 'Workout' || source === 'Survey' ? source : null,
    surveyAssignmentId: count(payload?.surveyAssignmentId),
  };
}

function recordFacts(payload: AttentionItemPayload | null | undefined): RecordFacts | null {
  const movements = count(payload?.prCount);
  if (movements === null) return null;
  return {
    movements,
    exerciseName: text(payload?.prExerciseName),
    exerciseId: count(payload?.prExerciseId),
  };
}

export function triageShape(payload: AttentionItemPayload | null | undefined): TriageFacts {
  return {
    sessionName: text(payload?.sessionName),
    sessionLocalDay: text(payload?.sessionLocalDay),
    sessionSetsDone: count(payload?.sessionSetsDone),
    sessionSetsTotal: count(payload?.sessionSetsTotal),
    planName: text(payload?.planName),
    planEndDate: text(payload?.planEndDate),
    goalDescription: text(payload?.goalDescription),
    goalDeadline: text(payload?.goalDeadline),
    daysPending: count(payload?.daysPending),
    messagePreview: text(payload?.messagePreview),
    planAssignmentId: count(payload?.planAssignmentId),
    goalId: count(payload?.goalId),
    invitationId: count(payload?.invitationId),
    leadEmailVerified: typeof payload?.publicFormLeadEmailVerified === 'boolean' ? payload.publicFormLeadEmailVerified : null,
    vital: vitalFacts(payload),
    record: recordFacts(payload),
  };
}
